/**
 * @fileoverview Downloadable component for displaying a list of downloadable items
 *
 * @example
 * // Basic usage
 * <Downloadable list={[{ icon: 'file', link: 'https://example.com/file.pdf', title: 'File' }]} />
 */

import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../Icon'
import { TextLink } from '../../typography/Typography'

import css from './Downloadable.module.css'

/**
 * @interface DownloadableProps
 * @description Props for the Downloadable component
 * @property {Novoic.File[]} list - The list of downloadable items
 */
export interface DownloadableProps extends PropsWithChildren {
    list: Novoic.File[]
}

/**
 * @function Downloadable
 * @description Component for displaying a list of downloadable items
 * @param {DownloadableProps} props - The props for the component
 * @returns {JSX.Element} The Downloadable component
 */
export function Downloadable({ list }: DownloadableProps) {
    const { t } = useTranslation()
    return (
        <ul className={css.list}>
            {list.map((item: Novoic.File, index: number) => {
                return (
                    <li key={`${item.icon}-${index}`}>
                        <Icon name={item.icon} />
                        <div className={css.text}>
                            <TextLink href={item.link}>{t(item.title)}</TextLink>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
